import { useSearchParams, Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader } from '@/components/layout/AppShell'
import { Card } from '@/components/ui/Card'
import { SearchInput } from '@/components/ui/Input'
import { Pagination, PageInfo } from '@/components/ui/Pagination'
import { OcrStatusBadge } from '@/components/shared/StatusBadge'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { useAttachments } from '@/hooks/useProOps'
import { cn } from '@/lib/utils'
import { ATTACHMENT_ROLE_LABELS } from '@/lib/constants'
import type { AttachmentRole } from '@/lib/pocketbase'

export function AttachmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') || ''
  const page = parseInt(searchParams.get('page') || '1')

  const { data, isLoading } = useAttachments({ search, page, perPage: 20 })

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('q', value)
    } else {
      params.delete('q')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  return (
    <div>
      <PageHeader
        title="เอกสารแนบ"
        description="เรียกดูเอกสารแนบของประกาศทั้งหมด"
      />

      <div className="mb-6">
        <SearchInput
          placeholder="ค้นหาตามชื่อไฟล์..."
          defaultValue={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="max-w-md"
        />
      </div>

      {data && (
        <div className="mb-4">
          <PageInfo
            currentPage={data.page}
            pageSize={data.perPage}
            totalItems={data.totalItems}
          />
        </div>
      )}

      {isLoading && <LoadingState />}

      {data && data.items.length === 0 && (
        <Card className="p-8 text-center">
          <Icon icon="solar:paperclip-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">ไม่พบเอกสารแนบ</h3>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="flex flex-col gap-3">
            {data.items.map((attachment: any) => (
              <AttachmentCard key={attachment.id} attachment={attachment} />
            ))}
          </div>
          <div className="mt-6">
            <Pagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  )
}

function AttachmentCard({ attachment }: { attachment: any }) {
  const fileExtension = attachment.file?.split('.').pop()?.toLowerCase() || ''
  const tender = attachment.expand?.tender_id

  const getFileIcon = (ext: string) => {
    const icons: Record<string, string> = {
      pdf: 'solar:document-text-line-duotone',
      doc: 'solar:document-line-duotone',
      docx: 'solar:document-line-duotone',
      xls: 'solar:chart-2-line-duotone',
      xlsx: 'solar:chart-2-line-duotone',
      jpg: 'solar:gallery-line-duotone',
      jpeg: 'solar:gallery-line-duotone',
      png: 'solar:gallery-line-duotone',
    }
    return icons[ext] || 'solar:file-line-duotone'
  }

  const getFileColor = (ext: string) => {
    const colors: Record<string, string> = {
      pdf: 'text-rose-600 bg-rose-100',
      doc: 'text-blue-600 bg-blue-100',
      docx: 'text-blue-600 bg-blue-100',
      xls: 'text-emerald-600 bg-emerald-100',
      xlsx: 'text-emerald-600 bg-emerald-100',
    }
    return colors[ext] || 'text-slate-600 bg-slate-100'
  }

  return (
    <Card hover>
      <div className="flex items-center gap-4">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', getFileColor(fileExtension))}>
          <Icon icon={getFileIcon(fileExtension)} className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-medium text-slate-900 truncate">{attachment.file}</p>
          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
            <span className="uppercase">{fileExtension}</span>
            {attachment.role && (
              <span>{ATTACHMENT_ROLE_LABELS[attachment.role as AttachmentRole] ?? attachment.role}</span>
            )}
            {attachment.published_at ? (
              <DateDisplay date={attachment.published_at} format="relative" />
            ) : (
              <span>-</span>
            )}
          </div>
          {tender && (
            <Link
              to={`/tenders/${tender?.id || attachment.tender_id || attachment.tender}`}
              className="text-sm text-blue-600 hover:text-blue-700 mt-1 inline-block"
            >
              {(tender?.project_name || tender?.title_th || 'ดูประกาศ').slice(0, 50)}
              {(tender?.project_name || tender?.title_th) ? '...' : ''}
            </Link>
          )}
        </div>
        {attachment.ocr_status && (
          <OcrStatusBadge status={attachment.ocr_status} />
        )}
      </div>
    </Card>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-slate-200 rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-48 bg-slate-200 rounded" />
              <div className="h-3 w-32 bg-slate-200 rounded" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}
